import { tr } from '@/contexts/LanguageContext';
// mediaPolicy — políticas configurables de subida (por contexto/rol)
//
// El Admin edita estos límites desde /admin/settings/global → Fotos y Videos.
// Cada upload (Feed, Curso, Producto, Admin, etc.) consulta su `policyContext`
// para conocer el `videoMaxSec` / `videoMaxMB` / `imageMaxMB` aplicables.
//
// `null` significa SIN LÍMITE (típico para cursos: video sin tope de duración/peso).
//
// Storage: lineage_admin_media_policy

const STORAGE_KEY = 'lineage_admin_media_policy';

// ============================================================================
// Lista canónica de contextos. Agrega entradas aquí si creas nuevos uploaders.
// ============================================================================
//
// Esquema por contexto:
//   imageMaxMB, videoMaxMB, videoMaxSec   — límites de tamaño/duración (null = sin límite)
//   imageQualityDefault    — preset del cropper preseleccionado ('fhd' | '4k' | '8k')
//   imageQualityOptions    — array de presets disponibles para que el user elija
//   imageQualityLocked     — si true, oculta el selector de calidad en el cropper
//   videoQualityDefault    — calidad de video sugerida ('sd' | 'hd' | 'fhd' | 'qhd' | '4k')
//   videoQualityOptions    — array (cuando length>1, el user puede elegir)
//   videoQualityLocked     — si true, no se muestra chooser (calidad forzada)
//   videoMinHeight         — alerta si el video subido tiene menos altura que este valor
//
export const MEDIA_POLICY_CONTEXTS = [
  // ============ BARBER ============
  {
    id: 'feed_post', role: 'barber', label: tr("Feed — post (foto/video)"),
    description: tr("Posts sociales del barbero al Ziyup Feed."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 700, videoMaxSec: 60,
      imageQualityDefault: '4k', imageQualityOptions: ['4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd'], videoQualityLocked: true,
      videoMinHeight: 720
    }
  },
  {
    id: 'reel', role: 'barber', label: tr("Feed — reel/video corto"),
    description: tr("Videos verticales tipo TikTok."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 700, videoMaxSec: 60,
      imageQualityDefault: '4k', imageQualityOptions: ['4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd', 'qhd'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  },
  {
    id: 'portfolio_photo', role: 'barber', label: tr("Portafolio (foto)"),
    description: tr("Foto del portafolio profesional del barbero."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: null, videoMaxSec: null,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd'], videoQualityLocked: true,
      videoMinHeight: 720
    }
  },
  {
    id: 'before_after', role: 'barber', label: tr("Antes y Después"),
    description: tr("Transformaciones de cliente."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: null, videoMaxSec: null,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd'], videoQualityLocked: true,
      videoMinHeight: 720
    }
  },
  {
    id: 'battle_content', role: 'barber', label: tr("Contenido de batalla 1vs1"),
    description: tr("Foto/video asociados a una batalla."),
    defaults: {
      imageMaxMB: 100, videoMaxMB: 1500, videoMaxSec: 600,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd', '4k'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  },
  {
    id: 'tournament_content', role: 'barber', label: tr("Contenido de torneo"),
    description: tr("Foto/video asociados a un torneo."),
    defaults: {
      imageMaxMB: 100, videoMaxMB: 1500, videoMaxSec: 600,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd', '4k'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  },

  // ============ COURSE / PRODUCT ============
  {
    id: 'course_video', role: 'barber', label: tr("Curso — video de lección"),
    description: tr("Videos educativos del curso. SIN límite + el usuario elige calidad."),
    defaults: {
      imageMaxMB: 100, videoMaxMB: null, videoMaxSec: null,
      imageQualityDefault: 'fhd', imageQualityOptions: ['fhd', '4k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd', '4k'], videoQualityLocked: false,
      videoMinHeight: 480
    }
  },
  {
    id: 'course_thumbnail', role: 'barber', label: tr("Curso — portada/thumbnail"),
    description: tr("Imagen de portada del curso."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: null, videoMaxSec: null,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd'], videoQualityLocked: true,
      videoMinHeight: 720
    }
  },
  {
    id: 'course_preview_video', role: 'barber', label: tr("Curso — video preview/trailer"),
    description: tr("Trailer del curso. El usuario elige calidad."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: null, videoMaxSec: null,
      imageQualityDefault: 'fhd', imageQualityOptions: ['fhd', '4k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd', '4k'], videoQualityLocked: false,
      videoMinHeight: 480
    }
  },
  {
    id: 'product_photo', role: 'barber', label: tr("Producto — foto"),
    description: tr("Fotos de la galería del producto."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: null, videoMaxSec: null,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd'], videoQualityLocked: true,
      videoMinHeight: 720
    }
  },
  {
    id: 'product_video', role: 'barber', label: tr("Producto — video demostrativo"),
    description: tr("Video del producto en uso."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 500, videoMaxSec: 300,
      imageQualityDefault: 'fhd', imageQualityOptions: ['fhd', '4k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  },

  // ============ BRAND ============
  {
    id: 'brand_post', role: 'brand', label: tr("Feed de marca (orgánico)"),
    description: tr("Posts orgánicos de marcas — no requieren aprobación."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 700, videoMaxSec: 90,
      imageQualityDefault: '4k', imageQualityOptions: ['4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd'], videoQualityLocked: true,
      videoMinHeight: 720
    }
  },

  // ============ CLIENT ============
  {
    id: 'client_review', role: 'client', label: tr("Reseña con foto/video"),
    description: tr("Reseñas visuales del cliente sobre un servicio."),
    defaults: {
      imageMaxMB: 25, videoMaxMB: 300, videoMaxSec: 60,
      imageQualityDefault: 'fhd', imageQualityOptions: ['fhd', '4k'], imageQualityLocked: false,
      videoQualityDefault: 'hd', videoQualityOptions: ['hd', 'fhd'], videoQualityLocked: false,
      videoMinHeight: 480
    }
  },
  {
    id: 'client_avatar', role: 'client', label: tr("Foto de perfil del cliente"),
    description: tr("Avatar del cliente."),
    defaults: {
      imageMaxMB: 10, videoMaxMB: null, videoMaxSec: null,
      imageQualityDefault: 'fhd', imageQualityOptions: ['fhd'], imageQualityLocked: true,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd'], videoQualityLocked: true,
      videoMinHeight: 480
    }
  },

  // ============ ADMIN ============
  {
    id: 'admin_news', role: 'admin', label: 'Admin — noticia',
    description: tr("Noticias oficiales de la plataforma."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 1500, videoMaxSec: 300,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd', '4k'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  },
  {
    id: 'admin_announcement', role: 'admin', label: tr("Admin — comunicado"),
    description: tr("Comunicados oficiales."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 1500, videoMaxSec: 300,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd', '4k'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  },
  {
    id: 'admin_banner', role: 'admin', label: tr("Admin — banner principal"),
    description: tr("Banners hero del Feed/Tournaments."),
    defaults: {
      imageMaxMB: 25, videoMaxMB: null, videoMaxSec: null,
      imageQualityDefault: '4k', imageQualityOptions: ['4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd'], videoQualityLocked: true,
      videoMinHeight: 720
    }
  },
  {
    id: 'admin_ad', role: 'admin', label: tr("Admin — publicidad"),
    description: tr("Publicidad interna o sponsored."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 700, videoMaxSec: 60,
      imageQualityDefault: '4k', imageQualityOptions: ['4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['fhd', 'qhd'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  },
  {
    id: 'admin_promo', role: 'admin', label: tr("Admin — promoción"),
    description: tr("Promociones de torneos/cursos/productos."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 700, videoMaxSec: 90,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k', '8k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  },
  {
    id: 'admin_video_feat', role: 'admin', label: tr("Admin — video destacado"),
    description: tr("Videos destacados de la plataforma."),
    defaults: {
      imageMaxMB: 50, videoMaxMB: 2000, videoMaxSec: 600,
      imageQualityDefault: '4k', imageQualityOptions: ['fhd', '4k'], imageQualityLocked: false,
      videoQualityDefault: 'fhd', videoQualityOptions: ['hd', 'fhd', 'qhd', '4k'], videoQualityLocked: false,
      videoMinHeight: 720
    }
  }
];

// Defaults completos (forma final que se persiste)
export const buildDefaultPolicy = () => {
  const out = {};
  MEDIA_POLICY_CONTEXTS.forEach((c) => { out[c.id] = { ...c.defaults }; });
  return out;
};

// ============================================================================
// Storage helpers
// ============================================================================
const readStore = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildDefaultPolicy();
    const parsed = JSON.parse(raw);
    // Merge con defaults para que nuevos contextos aparezcan automáticamente
    return { ...buildDefaultPolicy(), ...parsed };
  } catch {
    return buildDefaultPolicy();
  }
};

const writeStore = (policy) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(policy));
  window.dispatchEvent(new Event('lineage_admin_media_policy_updated'));
};

// ============================================================================
// API pública
// ============================================================================
export const getMediaPolicy = () => readStore();

export const getPolicyForContext = (contextId) => {
  if (!contextId) return null;
  const all = readStore();
  return all[contextId] || null;
};

export const updatePolicyForContext = (contextId, patch) => {
  const all = readStore();
  all[contextId] = { ...(all[contextId] || {}), ...patch };
  writeStore(all);
};

export const resetMediaPolicy = () => writeStore(buildDefaultPolicy());

export const resetContextToDefault = (contextId) => {
  const ctx = MEDIA_POLICY_CONTEXTS.find((c) => c.id === contextId);
  if (!ctx) return;
  const all = readStore();
  all[contextId] = { ...ctx.defaults };
  writeStore(all);
};

// Helpers de formato
export const formatLimit = (value, unit) => {
  if (value === null || value === undefined) return 'Ilimitado';
  if (unit === 'sec') {
    if (value >= 60) {
      const m = Math.floor(value / 60);
      const s = value % 60;
      return s ? `${m} min ${s} s` : `${m} min`;
    }
    return `${value} s`;
  }
  if (unit === 'MB') return `${value} MB`;
  return String(value);
};
